import React, { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { appointmentService } from '../../services/appointmentService';
import { inspectionService } from '../../services/inspectionService';
import { useLanguage } from '../../hooks/useLanguage';
import { SlotPicker } from './SlotPicker';
import { errorCode, todayKey } from './appointmentUtils';

interface Props {
    onClose: () => void;
    onCreated: () => void;
    busyInspectionIds: string[];  // inspections qui ont déjà un RDV actif
    defaultDuration: number;
}

const TYPES = ['REPAIR', 'MAINTENANCE', 'DIAGNOSTIC', 'OIL_CHANGE', 'TIRES', 'BODY_WORK', 'ELECTRICAL', 'OTHER'];

const isAcceptedQuote = (q: any, garageId: string) =>
    String(q.garageId) === String(garageId) && ['accepted_no_payment', 'accepted_with_payment'].includes(q.status);

export const NewAppointmentModal: React.FC<Props> = ({ onClose, onCreated, busyInspectionIds, defaultDuration }) => {
    const { t, isRTL } = useLanguage();
    const garageId: string = useMemo(() => {
        try { return JSON.parse(localStorage.getItem('user') || '{}')._id || ''; } catch { return ''; }
    }, []);
    const [inspections, setInspections] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [inspectionId, setInspectionId] = useState('');
    const [type, setType] = useState('REPAIR');
    const [duration, setDuration] = useState(defaultDuration);
    const [date, setDate] = useState(todayKey());
    const [timeSlot, setTimeSlot] = useState('');
    const [description, setDescription] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const all = await inspectionService.getAllInspectionsByGarageId();
                if (cancelled) return;
                // Devis accepté, travaux pas encore commencés (statut « accepted »), client non anonymisé avec compte Inspecly, sans rendez-vous en cours
                setInspections((all || []).filter((i: any) =>
                    i.status === 'accepted' && i.clientName !== 'Client anonymisé' && (i.quotes || []).some((q: any) => isAcceptedQuote(q, garageId)) && !!i.clientId && !busyInspectionIds.includes(String(i._id))));
            } catch {
                if (!cancelled) setInspections([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [garageId, busyInspectionIds]);

    const selected = inspections.find(i => String(i._id) === inspectionId);
    const canSubmit = !!timeSlot && !!inspectionId;

    const submit = async () => {
        setSaving(true); setError('');
        try {
            // Le garage PROPOSE un créneau : le client confirme dans l'app
            await appointmentService.createAppointment({ inspectionId, type, duration, date, timeSlot, description });
            onCreated();
            onClose();
        } catch (err) {
            setError(t(`appointments.errors.${errorCode(err)}`, { defaultValue: t('appointments.errors.generic') }));
        } finally {
            setSaving(false);
        }
    };

    const input = 'w-full rounded-xl border border-gray-300 bg-white px-3.5 py-2.5 text-sm focus:border-indigo-500 focus:ring-indigo-500';

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose} role="dialog" aria-modal="true">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto" dir={isRTL ? 'rtl' : 'ltr'} onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 sticky top-0 bg-white z-10">
                    <h2 className="text-lg font-bold text-gray-900">{t('appointments.new')}</h2>
                    <button onClick={onClose} aria-label={t('appointments.actions.close')} className="p-2 rounded-full hover:bg-gray-100"><X size={18} /></button>
                </div>

                <div className="p-6 space-y-5">
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">{t('appointments.form.inspection')}</label>
                        <select value={inspectionId} onChange={e => setInspectionId(e.target.value)} className={input} disabled={loading}>
                            <option value="">{loading ? t('appointments.form.loadingInspections') : t('appointments.form.selectInspection')}</option>
                            {inspections.map(i => (
                                <option key={i._id} value={i._id}>
                                    {[i.vehicleBrand, i.vehicleModel].filter(Boolean).join(' ')} — {i.clientName}
                                </option>
                            ))}
                        </select>
                        {!loading && inspections.length === 0 && <p className="text-sm text-gray-500 mt-2">{t('appointments.form.noAccepted')}</p>}
                        {selected && (
                            <p className="text-sm text-gray-600 mt-2 bg-gray-50 rounded-lg px-3 py-2">
                                {selected.clientName}{selected.clientPhone ? ` · ${selected.clientPhone}` : ''} · {[selected.vehicleBrand, selected.vehicleModel, selected.vehicleYear].filter(Boolean).join(' ')}
                            </p>
                        )}
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1.5">{t('appointments.form.type')}</label>
                            <select value={type} onChange={e => setType(e.target.value)} className={input}>
                                {TYPES.map(k => <option key={k} value={k}>{t(`appointments.type.${k}`)}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1.5">{t('appointments.form.duration')}</label>
                            <input type="number" min={15} max={720} step={15} value={duration} onChange={e => { setDuration(Number(e.target.value) || defaultDuration); setTimeSlot(''); }} className={input} />
                            <p className="text-xs text-gray-500 mt-1">{t('appointments.form.durationHint')}</p>
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">{t('appointments.form.dateAndTime')}</label>
                        <SlotPicker date={date} value={timeSlot} duration={duration} onDateChange={setDate} onChange={setTimeSlot} />
                    </div>

                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">{t('appointments.form.note')}</label>
                        <textarea value={description} onChange={e => setDescription(e.target.value)} rows={2} className={input} />
                    </div>

                    <p className="rounded-xl bg-indigo-50 px-4 py-3 text-sm text-indigo-900">{t('appointments.form.proposeHint')}</p>

                    {error && <p role="alert" className="text-sm text-red-700 bg-red-50 rounded-lg px-3 py-2">{error}</p>}
                </div>

                <div className="flex justify-end gap-2 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
                    <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100">{t('appointments.actions.close')}</button>
                    <button disabled={!canSubmit || saving} onClick={submit} className="px-5 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50">
                        {saving ? t('appointments.form.saving') : t('appointments.form.create')}
                    </button>
                </div>
            </div>
        </div>
    );
};
